// /**
//  * Sku Inventory List
//  */

// React
import React from 'react';

// UI
import {
  createStyles,
  makeStyles,
  Theme,
  withStyles,
} from '@material-ui/core/styles';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
} from '@material-ui/core';

// Type
import { SkuInventory } from '../../warehouses/interfaces';
import { Link } from 'react-router-dom';
import Icon from '@mdi/react';
import { mdiConsolidate, mdiFileDocumentOutline, mdiHistory, mdiPencil, mdiChevronDown, mdiChevronUp, mdiDownload } from '@mdi/js';
import theme from '../../../theme';
import stableSort from '../../../utils/Table/stableSort';
import getComparator from '../../../utils/Table/getComparator';

enum Type {
  skus = 'skus',
  warehouse = 'warehouse',
}

type Data = SkuInventory;
type Props = {
  data: any;
  type: Type;
  openCartexModal: (id: number) => void;
  handleReport: (id: number) => void;
  editable: boolean;
  downloadImage: (id:number, name: string) => void;
};

type Ord = 'asc' | 'desc';

interface HeadCell {
  id: keyof Data;
  label: string;
}

interface EnhancedTableProps {
  classes: ReturnType<typeof useStyles>;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => void;
  order: Ord;
  orderBy: string;
  rowCount: number;
}

// Style
const StyledTableCell = withStyles((theme: Theme) =>
  createStyles({
    head: {
      whiteSpace: 'nowrap',
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    body: {
      whiteSpace: 'nowrap',
      fontSize: 14,
    },
  })
)(TableCell);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      '& .MuiTableCell-body': {
        whiteSpace: 'nowrap',
      },
    },
    paper: {
      width: '100%',
    },
    table: {
      minWidth: 750,
    },
    columnText: {
      padding: '0 10px',
    },
    head: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    visuallyHidden: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1,
    },
    dot: {
      position: 'relative',
      display: 'inline-block',
      top: '0',
      left: '-8px',
      width: '8px',
      height: '8px',
      borderRadius: '50%',
    },
  })
);

const IconSort = (order: Ord) => {
  return (
    <Icon path={order === 'asc' ? mdiChevronDown : mdiChevronUp} size={1} />
  );
};

const headCells: HeadCell[] = [
  {
    id: 'partnerName',
    label: 'شرکت',
  },
  {
    id: 'productName',
    label: 'نام کالا',
  },
  {
    id: 'productCode',
    label: 'کد کالا',
  },
  {
    id: 'variantId',
    label: 'شناسه تنوع',
  },
  {
    id: 'variantCode',
    label: 'کد تنوع',
  },
  {
    id: 'sellerName',
    label: 'فروشنده/تامین‌کننده ',
  },
  {
    id: 'sellerId',
    label: 'کد فروشنده/تامین‌کننده',
  },
  {
    id: 'amount',
    label: 'موجودی',
  },
];

function EnhancedTableHead(props: EnhancedTableProps) {
  const { order, orderBy, onRequestSort } = props;
  const classes = useStyles();
  const createSortHandler =
    (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        {headCells.map(headCell => {
          const orderStatus = orderBy === headCell.id ? order : 'asc';
          return (
            <StyledTableCell
              key={headCell.id}
              align="center"
              sortDirection={orderBy === headCell.id ? order : false}
              data-cy={headCell.id}
            >
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
                IconComponent={() => IconSort(orderStatus)}
              >
                <span className={classes.columnText}>{headCell.label}</span>
              </TableSortLabel>
            </StyledTableCell>
          );
        })}
        <StyledTableCell />
      </TableRow>
    </TableHead>
  );
}

export default function EnhancedTable(props: Props) {
  const classes = useStyles();
  const [order, setOrder] = React.useState<Ord>('desc');
  const [orderBy, setOrderBy] = React.useState<keyof Data>('amount');
  const [dense] = React.useState(false);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <div className={classes.root}>
      <TableContainer component={Paper}>
        <Table
          className={classes.table}
          aria-labelledby="tableTitle"
          size={dense ? 'small' : 'medium'}
          aria-label="enhanced table"
        >
          <EnhancedTableHead
            classes={classes}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={props.data.length}
          />
          <TableBody>
            {stableSort(props.data, getComparator(order, orderBy))
              .slice()
              .map((row: any, index: number) => {
                const labelId = `enhanced-table-checkbox-${index}`;

                return (
                  <TableRow key={index}>
                    <TableCell align="center">{row.partnerName}</TableCell>
                    <TableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      align="center"
                    >
                      {row.productName}
                    </TableCell>
                    <TableCell align="center">{row.productCode}</TableCell>
                    <TableCell align="center">{row.id}</TableCell>
                    <TableCell align="center">{row.code}</TableCell>
                    <TableCell align="center">
                      {row.sellerName ? row.sellerName : row.providerName}
                    </TableCell>
                    <TableCell align="center">
                      {row.sellerCode ? row.sellerCode : row.providerCode}
                    </TableCell>
                    <TableCell align="center">{row.amount}</TableCell>
                    <TableCell align="center" style={{ display: "flex", alignItems: "stretch" }}>
                      {props.editable && <Link to={`/edit-product/${row.productId}`}>
                        <IconButton
                          aria-label="print"
                          size="small"
                          title="ویرایش محصول"
                          data-cy="print"
                        >
                          <Icon
                            path={mdiPencil}
                            size={1}
                            color={theme.palette.success.main}
                          />
                        </IconButton>
                      </Link>}
                      <IconButton
                        aria-label="report-products"
                        size="small"
                        title="تاریخچه ورود و خروج"
                        data-cy="report-products"
                        onClick={() => props.handleReport(row.id)}
                      >
                        <Icon
                          path={mdiConsolidate}
                          size={1}
                          color={theme.palette.blue.main}
                        />
                      </IconButton>
                      <IconButton
                        aria-label="detail"
                        size="small"
                        title="کارتکس"
                        data-cy="detail"
                        onClick={() => props.openCartexModal(row.id)}
                      >
                        <Icon
                          path={mdiFileDocumentOutline}
                          size={1}
                          color={theme.palette.green.main}
                        />
                      </IconButton>
                      {row.imageUrl && <a href={row.imageUrl} target="_blank">
                        <img width="30" height="33" src={row.imageUrl} />
                      </a>}
                      {row.imageFileName && <IconButton onClick={() => props.downloadImage(row.productId, row.imageFileName)}>
                        <Icon path={mdiDownload} size={1}/>  
                      </IconButton>}
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}
